import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "BOI Facilitation Quiz";
const PROJECT_SUBTITLE = "Facilitation operating system";

export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      logger.debug("[login] success → redirecting to /home", result);
      navigate("/home");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 403 && detail === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="boi-supergraphic w-full max-w-5xl overflow-hidden border-[0.5px] shadow-none">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
          <div className="bg-primary text-primary-foreground flex flex-col justify-between p-8 sm:p-10 lg:min-h-[680px] lg:p-12">
            <div className="space-y-6">
              <p className="boi-eyebrow text-primary-foreground/70">{PROJECT_SUBTITLE}</p>
              <div className="max-w-xl space-y-5">
                <h1 className="boi-display font-heading text-4xl leading-none sm:text-6xl lg:text-[5.4rem]">
                  Run sharper quiz sessions with Board of Innovation.
                </h1>
                <p className="max-w-md text-sm leading-6 text-primary-foreground/72 sm:text-base">
                  Author, host, and adapt Kahoot-style facilitation moments inside one reusable
                  platform built for BOI teams.
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-8">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="border-primary-foreground/15 rounded-2xl border px-4 py-4">
                  <p className="boi-eyebrow text-primary-foreground/60">Modular</p>
                  <p className="mt-2 text-sm text-primary-foreground/80">
                    Reuse structures across sessions without cloning the product.
                  </p>
                </div>
                <div className="border-primary-foreground/15 rounded-2xl border px-4 py-4">
                  <p className="boi-eyebrow text-primary-foreground/60">Facilitator-led</p>
                  <p className="mt-2 text-sm text-primary-foreground/80">
                    Keep teams focused with clean prompts and fast transitions.
                  </p>
                </div>
                <div className="border-primary-foreground/15 rounded-2xl border px-4 py-4">
                  <p className="boi-eyebrow text-primary-foreground/60">Reusable</p>
                  <p className="mt-2 text-sm text-primary-foreground/80">
                    Build once, refine often, and deploy across BOI engagements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background flex items-center p-6 sm:p-8 lg:p-12">
            <div className="mx-auto w-full max-w-md">
              <CardHeader className="px-0 pt-0 text-left">
                <p className="boi-eyebrow text-muted">Board of Innovation internal</p>
                <CardTitle className="boi-display font-heading text-primary mt-3 text-3xl sm:text-4xl">
                  {PROJECT_NAME}
                </CardTitle>
                <p className="text-muted mt-3 text-sm leading-6">
                  Sign in to launch facilitation quizzes, manage session content, and keep every
                  workshop on pace.
                </p>
              </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-foreground text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <label htmlFor="rememberMe" className="text-foreground text-sm">
                Remember me for 30 days
              </label>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
            <p className="text-muted text-center text-sm">
              Don&apos;t have an account?{" "}
              <Link to="/signup" className="text-accent hover:underline">
                Sign up
              </Link>
            </p>
          </form>
        </CardContent>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
